import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { usePageContext } from '../context/PageContext';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import {
  ArrowLeft, FileText, Download, Trash2, Clock, CheckCircle,
  ChevronRight, ChevronDown, Edit3, Save, X, RefreshCw, AlertCircle
} from 'lucide-react';

const PolicyDetail = () => {
  const { documentId } = useParams();
  const { organizationId } = useAuth();
  const { theme } = useTheme();
  const { setSubtitle } = usePageContext();
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  const [doc, setDoc] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('sections');
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [selectedSection, setSelectedSection] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [reParsing, setReParsing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const cardClass = `rounded-lg border ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`;

  useEffect(() => {
    if (!organizationId || !documentId) return;
    loadDocument();
    return () => setSubtitle('');
  }, [organizationId, documentId]);

  const loadDocument = async () => {
    setLoading(true);
    try {
      const [docRes, sectionsRes] = await Promise.all([
        supabase.from('policy_documents').select('*').eq('id', documentId).eq('organization_id', organizationId).single(),
        supabase.from('document_sections').select('*').eq('document_id', documentId).order('sort_order'),
      ]);
      if (docRes.error) throw docRes.error;
      setDoc(docRes.data);
      setSections(sectionsRes.data || []);
      if (docRes.data) {
        setSubtitle(docRes.data.name);
      }
      // Auto-expand top-level sections
      const topLevel = new Set((sectionsRes.data || []).filter(s => s.depth === 0).map(s => s.id));
      setExpandedSections(topLevel);
    } catch (e) {
      console.error('Error loading document:', e);
    }
    setLoading(false);
  };

  const handleReParse = async () => {
    if (!doc?.full_text) {
      toast.error('No text content available to parse');
      return;
    }
    setReParsing(true);
    try {
      // Delete existing sections
      await supabase.from('document_sections').delete().eq('document_id', documentId);

      // Re-parse
      const text = doc.full_text;
      const lines = text.split('\n').filter(l => l.trim());
      const newSections = [];
      let currentSection = null;
      let sortOrder = 0;

      for (const line of lines) {
        const trimmed = line.trim();
        const sectionMatch = trimmed.match(/^(?:(?:Section|Clause|Part|Article)\s+)?(\d+(?:\.\d+)*)[.:)\s]\s*(.*)$/i);
        const headingMatch = !sectionMatch && trimmed.match(/^(#{1,4})\s+(.+)$|^([A-Z][A-Z\s]{3,})$/);

        if (sectionMatch) {
          if (currentSection) newSections.push(currentSection);
          const sectionNum = sectionMatch[1];
          const rest = sectionMatch[2] || '';
          const depth = sectionNum.split('.').length - 1;
          currentSection = {
            document_id: documentId, organization_id: organizationId,
            section_number: sectionNum, section_type: depth === 0 ? 'section' : depth === 1 ? 'subsection' : 'paragraph',
            title: rest.length > 0 && rest.length < 120 ? rest : null,
            content: rest.length >= 120 ? rest : '', depth, sort_order: sortOrder++,
          };
        } else if (headingMatch) {
          if (currentSection) newSections.push(currentSection);
          const title = headingMatch[2] || headingMatch[3];
          currentSection = {
            document_id: documentId, organization_id: organizationId,
            section_number: null, section_type: 'section', title: title.trim(),
            content: '', depth: 0, sort_order: sortOrder++,
          };
        } else if (currentSection) {
          currentSection.content += (currentSection.content ? '\n' : '') + trimmed;
        } else {
          currentSection = {
            document_id: documentId, organization_id: organizationId,
            section_number: null, section_type: 'paragraph', title: null,
            content: trimmed, depth: 0, sort_order: sortOrder++,
          };
        }
      }
      if (currentSection) newSections.push(currentSection);

      const validSections = newSections.filter(s => s.content || s.title).map(s => ({ ...s, content: s.content || s.title || '' }));
      if (validSections.length > 0) {
        const { data } = await supabase.from('document_sections').insert(validSections).select();
        setSections(data || []);
        await supabase.from('policy_documents').update({ is_parsed: true, parsed_at: new Date().toISOString() }).eq('id', documentId);
      }

      toast.success(`Parsed into ${validSections.length} sections`);
      loadDocument();
    } catch (e) {
      toast.error('Failed to re-parse');
    }
    setReParsing(false);
  };

  const handleSaveSection = async () => {
    if (!editingSection) return;
    try {
      await supabase.from('document_sections').update({ content: editContent }).eq('id', editingSection);
      toast.success('Section updated');
      setEditingSection(null);
      loadDocument();
    } catch {
      toast.error('Failed to save');
    }
  };

  const handleDeleteDocument = async () => {
    try {
      await supabase.from('document_sections').delete().eq('document_id', documentId);
      await supabase.from('policy_documents').delete().eq('id', documentId);
      toast.success('Document deleted');
      navigate('/policies');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const toggleSection = (id) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className={`${cardClass} h-24 animate-pulse p-5`} />
        <div className={`${cardClass} h-64 animate-pulse p-5`} />
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="space-y-6">
        <button onClick={() => navigate('/policies')} className={`flex items-center gap-2 text-sm ${isDark ? 'text-[#a1a1aa] hover:text-white' : 'text-[#666] hover:text-[#1a1a1a]'}`}>
          <ArrowLeft className="h-4 w-4" /> Back to policies
        </button>
        <div className={`text-center py-12 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Document not found</div>
      </div>
    );
  }

  const typeColor = {
    policy: 'text-blue-500', contract: 'text-purple-500',
    handbook: 'text-emerald-500', guideline: 'text-amber-500',
  }[doc.document_type] || 'text-blue-500';

  return (
    <div className="space-y-6">
      {/* Back */}
      <button onClick={() => navigate('/policies')} className={`flex items-center gap-2 text-sm ${isDark ? 'text-[#a1a1aa] hover:text-white' : 'text-[#666] hover:text-[#1a1a1a]'}`}>
        <ArrowLeft className="h-4 w-4" /> Back to policies
      </button>

      {/* Document Header */}
      <div className={`${cardClass} p-5`}>
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <FileText className={`h-5 w-5 ${typeColor}`} />
              <h1 className={`text-xl font-semibold font-[Manrope] ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{doc.name}</h1>
            </div>
            {doc.description && (
              <p className={`text-sm mb-3 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>{doc.description}</p>
            )}
            <div className="flex items-center gap-4 flex-wrap">
              <span className={`text-xs px-2 py-0.5 rounded ${typeColor.replace('text-', 'bg-').replace('500', '500/10')} ${typeColor}`}>
                {doc.document_type}
              </span>
              {doc.version && <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Version {doc.version}</span>}
              {doc.file_name && <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>{doc.file_name}</span>}
              {doc.effective_date && (
                <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                  Effective: {new Date(doc.effective_date).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              )}
              {doc.expiry_date && (
                <span className={`text-xs ${new Date(doc.expiry_date) < new Date() ? 'text-red-400' : isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                  {new Date(doc.expiry_date) < new Date() ? 'Expired' : 'Expires'}: {new Date(doc.expiry_date).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              )}
              <span className={`text-xs flex items-center gap-1 ${doc.is_parsed ? 'text-emerald-500' : isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                {doc.is_parsed ? <><CheckCircle className="h-3 w-3" /> Parsed ({sections.length} sections)</> : <><Clock className="h-3 w-3" /> Not parsed</>}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {doc.full_text && (
              <button
                onClick={handleReParse}
                disabled={reParsing}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border ${isDark ? 'border-[#27272a] text-[#a1a1aa] hover:bg-[#18181b]' : 'border-[#e5e5e5] text-[#666] hover:bg-[#f5f5f5]'} disabled:opacity-50`}
              >
                <RefreshCw className={`h-3 w-3 ${reParsing ? 'animate-spin' : ''}`} /> Re-parse
              </button>
            )}
            {confirmDelete ? (
              <div className="flex items-center gap-2">
                <span className={`text-xs ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Delete this document?</span>
                <button onClick={handleDeleteDocument} className="px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-red-500 hover:bg-red-600">Yes, delete</button>
                <button onClick={() => setConfirmDelete(false)} className={`px-3 py-1.5 rounded-lg text-xs ${isDark ? 'text-[#a1a1aa] hover:bg-[#18181b]' : 'text-[#666] hover:bg-[#f5f5f5]'}`}>Cancel</button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmDelete(true)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border border-red-500/20 text-red-400 hover:bg-red-500/10`}
              >
                <Trash2 className="h-3 w-3" /> Delete
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className={`flex gap-1 border-b ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
        {[
          { id: 'sections', label: 'Sections', count: sections.length },
          { id: 'fulltext', label: 'Full Text' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-sm whitespace-nowrap transition-colors border-b-2 -mb-px ${
              activeTab === tab.id
                ? isDark ? 'border-[#7662ac] text-white' : 'border-[#586f8d] text-[#1a1a1a]'
                : `border-transparent ${isDark ? 'text-[#52525b] hover:text-[#a1a1aa]' : 'text-[#999] hover:text-[#666]'}`
            }`}
          >
            {tab.label}{tab.count !== undefined ? ` (${tab.count})` : ''}
          </button>
        ))}
      </div>

      {/* Sections Tab */}
      {activeTab === 'sections' && (
        <div className="flex gap-5">
          {/* Table of Contents sidebar */}
          <div className={`w-64 flex-shrink-0 ${cardClass} p-4 self-start sticky top-[76px] max-h-[calc(100vh-100px)] overflow-y-auto`}>
            <h3 className={`text-xs font-medium uppercase tracking-wider mb-3 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
              Contents
            </h3>
            {sections.length === 0 ? (
              <p className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>No sections parsed yet</p>
            ) : (
              <div className="space-y-0.5">
                {sections.map(s => (
                  <button
                    key={s.id}
                    onClick={() => {
                      setSelectedSection(s.id);
                      document.getElementById(`section-${s.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }}
                    className={`w-full text-left px-2 py-1.5 rounded text-xs transition-colors truncate ${
                      selectedSection === s.id
                        ? isDark ? 'bg-[#18181b] text-white' : 'bg-[#f0f0f0] text-[#1a1a1a]'
                        : isDark ? 'text-[#a1a1aa] hover:text-white hover:bg-[#18181b]/50' : 'text-[#666] hover:text-[#1a1a1a] hover:bg-[#f5f5f5]'
                    }`}
                    style={{ paddingLeft: `${8 + s.depth * 12}px` }}
                  >
                    {s.section_number && <span className={`font-mono mr-1.5 ${isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'}`}>{s.section_number}</span>}
                    {s.title || (s.content?.substring(0, 40) + '...')}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Section Content */}
          <div className="flex-1 space-y-3">
            {sections.length === 0 ? (
              <div className={`${cardClass} p-8 text-center`}>
                <AlertCircle className={`h-8 w-8 mx-auto mb-3 ${isDark ? 'text-[#27272a]' : 'text-[#e5e5e5]'}`} />
                <h3 className={`text-sm font-medium mb-1 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>No sections yet</h3>
                <p className={`text-xs mb-4 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                  {doc.full_text ? 'Click "Re-parse" to extract sections from the document text' : 'Upload a .txt file or paste text content to enable section parsing'}
                </p>
              </div>
            ) : (
              sections.map(s => (
                <div
                  key={s.id}
                  id={`section-${s.id}`}
                  className={`${cardClass} p-4 transition-all ${selectedSection === s.id ? isDark ? 'border-[#7662ac]/30' : 'border-[#586f8d]/30' : ''}`}
                  onClick={() => setSelectedSection(s.id)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {s.section_number && (
                        <span className={`text-xs font-mono px-1.5 py-0.5 rounded ${isDark ? 'bg-[#18181b] text-[#7662ac]' : 'bg-[#f5f5f5] text-[#586f8d]'}`}>
                          {s.section_number}
                        </span>
                      )}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${isDark ? 'bg-[#18181b] text-[#52525b]' : 'bg-[#f5f5f5] text-[#999]'}`}>
                        {s.section_type}
                      </span>
                      {s.title && (
                        <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{s.title}</span>
                      )}
                    </div>
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        if (editingSection === s.id) {
                          handleSaveSection();
                        } else {
                          setEditingSection(s.id);
                          setEditContent(s.content);
                        }
                      }}
                      className={`p-1 rounded ${isDark ? 'text-[#52525b] hover:text-white hover:bg-[#18181b]' : 'text-[#999] hover:text-[#1a1a1a] hover:bg-[#f5f5f5]'}`}
                    >
                      {editingSection === s.id ? <Save className="h-3.5 w-3.5" /> : <Edit3 className="h-3.5 w-3.5" />}
                    </button>
                  </div>
                  {editingSection === s.id ? (
                    <div>
                      <textarea
                        className={`w-full px-3 py-2 rounded-lg border text-sm min-h-[120px] ${isDark ? 'bg-[#09090b] border-[#27272a] text-white' : 'bg-white border-[#e5e5e5] text-[#1a1a1a]'} focus:outline-none focus:ring-1 focus:ring-[#586f8d]`}
                        value={editContent}
                        onChange={e => setEditContent(e.target.value)}
                      />
                      <div className="flex gap-2 mt-2">
                        <button onClick={handleSaveSection} className="px-3 py-1 text-xs rounded-md text-white accent-gradient">Save</button>
                        <button onClick={() => setEditingSection(null)} className={`px-3 py-1 text-xs rounded-md ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <p className={`text-sm whitespace-pre-wrap leading-relaxed ${isDark ? 'text-[#a1a1aa]' : 'text-[#555]'}`}>
                      {s.content}
                    </p>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Full Text Tab */}
      {activeTab === 'fulltext' && (
        <div className={`${cardClass} p-5`}>
          {doc.full_text ? (
            <pre className={`text-sm whitespace-pre-wrap leading-relaxed font-sans ${isDark ? 'text-[#a1a1aa]' : 'text-[#555]'}`}>
              {doc.full_text}
            </pre>
          ) : (
            <div className="text-center py-8">
              <FileText className={`h-8 w-8 mx-auto mb-3 ${isDark ? 'text-[#27272a]' : 'text-[#e5e5e5]'}`} />
              <p className={`text-sm ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                No text content available. Only .txt files can be parsed client-side.
              </p>
              <p className={`text-xs mt-1 ${isDark ? 'text-[#3f3f46]' : 'text-[#ccc]'}`}>
                PDF and DOCX parsing requires server-side processing.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PolicyDetail;
